import { useCallback } from 'react'
import type { FormikHelpers } from 'formik'
import { postData } from '@user/store/dashboard/widgets/gainMoreCustomers/api'
import { postData as upcomingFolowupsPostData } from '@user/store/dashboard/widgets/upcomingFollowUps/api'
import { postData as overdueNextActionDatesPostData } from '@user/store/dashboard/widgets/overdueNextActionDates/api'
import { postData as manageAgreementPostData } from '@user/store/dashboard/widgets/manageAgreements/api'
import { toast } from '@sales-center/components'
import { useAppDispatch } from '@user/store/hooks'
import {
  ActionCreatorWithoutPayload,
  ActionCreatorWithPayload,
} from '@reduxjs/toolkit'
import { updateAssigneeIdsNullValue } from '../utils/updateAssigneeIdsNullValue'

type GainCustomersFormValues = {
  assigneeIds: string[]
  salesTeamIds: number[]
}

type UpcomingFollowupsFormValue = {
  assigneeIds: string[]
  numDays: number
  salesTeamIds: number[]
}

type OverdueNextActionDatesValue = {
  assigneeIds: string[]
  salesTeamIds: number[]
}

type ManageAgreementsFormValue = {
  assigneeIds: string[]
  numDays: number
  salesTeamIds: number[]
}

type Props = {
  flipCard: ActionCreatorWithoutPayload<string>
  setAssigneeIds: ActionCreatorWithPayload<string[], string>
  setSalesTeamsIds: ActionCreatorWithPayload<number[], string>
}

export function useHandleSubmit(props: Props) {
  const { flipCard, setAssigneeIds, setSalesTeamsIds } = props
  const dispatch = useAppDispatch()

  const handleGainMoreCustomersSubmit = useCallback(
    async (
      values: GainCustomersFormValues,
      { setSubmitting }: FormikHelpers<GainCustomersFormValues>,
    ) => {
      setSubmitting(true)

      dispatch(setSalesTeamsIds(values.salesTeamIds))
      dispatch(setAssigneeIds(values.assigneeIds))

      await dispatch(
        postData({
          type: 'GMC',
          assigneeIds: updateAssigneeIdsNullValue(values.assigneeIds),
          salesTeamIds: values.salesTeamIds,
        }),
      )

      toast.success('Settings updated.')

      setSubmitting(false)
      dispatch(flipCard())
    },
    [dispatch, flipCard, setAssigneeIds, setSalesTeamsIds],
  )

  const handleUpcomingFollowupsSubmit = useCallback(
    async (
      values: UpcomingFollowupsFormValue,
      { setSubmitting }: FormikHelpers<UpcomingFollowupsFormValue>,
    ) => {
      setSubmitting(true)

      await dispatch(
        upcomingFolowupsPostData({
          type: 'UFU',
          assigneeIds: updateAssigneeIdsNullValue(values.assigneeIds),
          salesTeamIds: values.salesTeamIds,
          numberOfDays: values.numDays,
        }),
      )

      toast.success('Settings updated.')

      setSubmitting(false)
      dispatch(flipCard())
    },
    [dispatch, flipCard],
  )

  const handleOverdueNextActionDatesSubmit = useCallback(
    async (
      values: OverdueNextActionDatesValue,
      { setSubmitting }: FormikHelpers<OverdueNextActionDatesValue>,
    ) => {
      setSubmitting(true)

      await dispatch(
        overdueNextActionDatesPostData({
          type: 'ONA',
          assigneeIds: updateAssigneeIdsNullValue(values.assigneeIds),
          salesTeamIds: values.salesTeamIds,
        }),
      )

      toast.success('Settings updated.')

      setSubmitting(false)
      dispatch(flipCard())
    },
    [dispatch, flipCard],
  )

  const handleManageAgreementsSubmit = useCallback(
    async (
      values: ManageAgreementsFormValue,
      { setSubmitting }: FormikHelpers<ManageAgreementsFormValue>,
    ) => {
      setSubmitting(true)

      await dispatch(
        manageAgreementPostData({
          type: 'MC',
          assigneeIds: updateAssigneeIdsNullValue(values.assigneeIds),
          salesTeamIds: values.salesTeamIds,
          numberOfDays: values.numDays,
        }),
      )

      toast.success('Settings updated.')

      setSubmitting(false)
      dispatch(flipCard())
    },
    [dispatch, flipCard],
  )

  return {
    handleGainMoreCustomersSubmit,
    handleUpcomingFollowupsSubmit,
    handleOverdueNextActionDatesSubmit,
    handleManageAgreementsSubmit,
  }
}
